import { debounce } from "lodash";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState, useEffect } from "react";

import { Box } from '@mui/system';
import Stack from '@mui/material/Stack';
import Autocomplete from '@mui/material/Autocomplete';
import {
    Card,
    Button,
    TextField,
    CardHeader,
    Typography,
    CardContent
} from '@mui/material';

import { useBoolean } from "src/hooks/use-boolean";

import { useTranslate } from "src/locales";
import { EntityLabelType } from "src/services/utils/utils.types";
import { useSearchLabelsQuery } from "src/services/utils/utils.service";
import { useGetCompanyByIdQuery, useUpdateLocalCompanyMutation } from "src/services/connections/connections.service";

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import EmptyContent from "src/components/empty-content";
import { ConfirmDialog } from "src/components/custom-dialog";

type Props = {
    companyProfileId: string;
};

interface LabelOption {
    label: string;
    value: string;
}

export const CompanyLabelsCard = ({ companyProfileId }: Props) => {
    const { t } = useTranslate();

    const showEditorDialog = useBoolean(false);

    const [selectedLabels, setSelectedLabels] = useState<LabelOption[]>([]);
    const [labelSearchQuery, setLabelSearchQuery] = useState<string>('');

    const { currentData: companyProfile } = useGetCompanyByIdQuery({
        profile_id: companyProfileId as string
    });

    const { currentData: availableLabels } = useSearchLabelsQuery({
        label: labelSearchQuery ?? undefined,
        type: EntityLabelType.COMPANY,
        page: 1,
        per_page: 10
    });

    const [updateCompany, { isLoading: isUpdatingCompany }] = useUpdateLocalCompanyMutation();

    useEffect(() => {
        if (companyProfile?.labels) {
            const labels = companyProfile.labels.map((label) => ({
                label: label.label as string,
                value: label.id as string
            }));

            setSelectedLabels(labels);
        }
    }, [companyProfile, t]);

    const selectabelLabels = useMemo(() => {
        if (!availableLabels?.results) return [];

        return availableLabels.results.map((label) => ({
            label: label.label as string,
            value: label.id as string,
        }));
    }, [availableLabels]);

    const handleUpdateLabels = async () => {

        if (!companyProfileId) return;

        try {

            const labels = selectedLabels.map((label) => ({
                id: label.value
            }));

            await updateCompany({
                profile_id: companyProfileId,
                data: {
                    labels
                }
            }).unwrap();

            setSelectedLabels([]);

            enqueueSnackbar(t('companies.profile.api.update_labels.success'), {
                variant: 'success'
            });

            showEditorDialog.onFalse();
        }
        catch (error) {
            enqueueSnackbar(t('companies.profile.api.update_labels.default_error'), {
                variant: 'error'
            });
        }
    }

    const onSearchQueryChange = useMemo(
        () => debounce((value: string) => {
            setLabelSearchQuery(value);
        }, 500),
        [setLabelSearchQuery]
    );

    return (
        <Card>
            <CardHeader
                title={
                    <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
                        <Iconify icon="tabler:tags" />
                        <Box>{t('companies.profile.labels.title')}</Box>
                    </Stack>
                }
                action={
                    <Button
                        variant="outlined"
                        onClick={showEditorDialog.onTrue}
                        size='small'
                        startIcon={<Iconify icon="eva:edit-2-outline" />}
                    >

                        {t('companies.profile.labels.edit')}
                    </Button>
                }
            />
            <CardContent>
                <Box width="100%" >

                    {
                        companyProfile?.labels?.length === 0 && (
                            <Stack direction="row" spacing={2} width="100%" justifyContent="center" alignItems="center" flexWrap="wrap">
                                <EmptyContent
                                    title={t('companies.profile.labels.empty')}
                                    sx={{ width: '100%' }}
                                />
                            </Stack>
                        )
                    }

                    {
                        !!companyProfile?.labels?.length && (
                            <Stack direction="row" spacing={2} width="100%" justifyContent="start" alignItems="center" flexWrap="wrap">
                                {
                                    companyProfile?.labels?.map((label) => (
                                        <Label key={label.id as string} variant="soft">{label.label}</Label>
                                    ))
                                }
                            </Stack>
                        )
                    }


                </Box>
            </CardContent>

            <ConfirmDialog
                open={showEditorDialog.value}
                title={t('companies.profile.labels.dialog_title')}
                onClose={showEditorDialog.onFalse}
                content={
                    <Autocomplete
                        sx={{
                            paddingTop: 2,
                        }}
                        multiple
                        options={selectabelLabels}
                        value={selectedLabels}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t(
                                    'companies.profile.labels.select'
                                )}
                                placeholder={t(
                                    'companies.profile.labels.select_placeholder'
                                )}
                            />
                        )}
                        onInputChange={(_e, value) => onSearchQueryChange(value)}
                        onChange={(_e, value: LabelOption[]) => setSelectedLabels(value)}
                        renderOption={({ key, ...rest }, option: LabelOption) => (
                            <li key={key} {...rest}>
                                <Stack direction="column">
                                    <Typography variant="body1">
                                        {`${option.label}`}
                                    </Typography>
                                </Stack>
                            </li>
                        )}
                        getOptionLabel={(option) => option.label as string}
                        isOptionEqualToValue={(option: LabelOption, value: LabelOption) =>
                            option && option.value === value.value
                        }
                        getOptionKey={(option) => (option as LabelOption).value as string}
                    />
                }
                action={
                    <Button variant="contained" disabled={!selectabelLabels.length} onClick={handleUpdateLabels}>
                        {t('common.update')}
                    </Button>
                }
            />
        </Card>
    );
}